/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { nextFeedRefreshGeneration } from '../../core/internal/feed-refresh-order.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetProposalsRow, GetProposalRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { MarketBackingSchema, MarketCreatedNotificationSchema, MarketLifecycleSchema, OutcomeAddedSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetProposals ────────────────────────────────────────────────────────────

export interface useGetProposalsArgs { readonly marketType?: string; readonly status?: string; readonly excludeStatus?: string; readonly category?: string; readonly location?: string; readonly search?: string; readonly creator?: string; readonly pubkeys?: string; readonly fundingMin?: string; readonly fundingMax?: string; readonly stakedMin?: string; readonly stakedMax?: string; readonly backersMin?: string; readonly backersMax?: string; readonly outcomesMin?: string; readonly outcomesMax?: string; readonly createdAfter?: string; readonly createdBefore?: string; readonly sortBy?: string; readonly sortOrder?: string; readonly limit?: number | string; readonly viewportGated?: boolean; readonly fields?: readonly ('marketType' | 'partnerCreatorId' | 'associatedCreator' | 'pubkey' | 'marketConfig' | 'proposalId' | 'title' | 'description' | 'category' | 'location' | 'status' | 'totalFunded' | 'totalStaked' | 'creationThresholdPct' | 'backerCount' | 'numBackers' | 'coverImageUrl' | 'coverImageNsfw' | 'numOutcomes' | 'debateSpreadOk' | 'graduatedMarketPubkey' | 'createdAt' | 'updatedAt' | 'expiresAt' | 'upvotes' | 'downvotes' | 'netVotes' | 'myVote' | 'proposalScore' | 'outcomes' | 'proposer' | 'topBackers')[]; }


async function _useGetProposalsQueryFn(
  args: useGetProposalsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetProposalsRow>> {
  const url = '/v1/proposals';
  const params = new URLSearchParams();

  if (args.marketType !== undefined) params.set('marketType', String(args.marketType));
  if (args.status !== undefined) params.set('status', String(args.status));
  if (args.excludeStatus !== undefined) params.set('excludeStatus', String(args.excludeStatus));
  if (args.category !== undefined) params.set('category', String(args.category));
  if (args.location !== undefined) params.set('location', String(args.location));
  if (args.search !== undefined) params.set('search', String(args.search));
  if (args.creator !== undefined) params.set('creator', String(args.creator));
  if (args.pubkeys !== undefined) params.set('pubkeys', String(args.pubkeys));
  if (args.fundingMin !== undefined) params.set('fundingMin', String(args.fundingMin));
  if (args.fundingMax !== undefined) params.set('fundingMax', String(args.fundingMax));
  if (args.stakedMin !== undefined) params.set('stakedMin', String(args.stakedMin));
  if (args.stakedMax !== undefined) params.set('stakedMax', String(args.stakedMax));
  if (args.backersMin !== undefined) params.set('backersMin', String(args.backersMin));
  if (args.backersMax !== undefined) params.set('backersMax', String(args.backersMax));
  if (args.outcomesMin !== undefined) params.set('outcomesMin', String(args.outcomesMin));
  if (args.outcomesMax !== undefined) params.set('outcomesMax', String(args.outcomesMax));
  if (args.createdAfter !== undefined) params.set('createdAfter', String(args.createdAfter));
  if (args.createdBefore !== undefined) params.set('createdBefore', String(args.createdBefore));
  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? Number(args.limit ?? 24)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetProposals',
    routeTemplate: '/v1/proposals',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetProposalsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetProposalsFactory = Object.assign(
  (args: useGetProposalsArgs) => ({
    queryKey: ['proposals', 'list', { marketType: args.marketType ?? null, status: args.status ?? null, excludeStatus: args.excludeStatus ?? null, category: args.category ?? null, location: args.location ?? null, search: args.search ?? null, creator: args.creator ?? null, pubkeys: args.pubkeys ?? null, fundingMin: args.fundingMin ?? null, fundingMax: args.fundingMax ?? null, stakedMin: args.stakedMin ?? null, stakedMax: args.stakedMax ?? null, backersMin: args.backersMin ?? null, backersMax: args.backersMax ?? null, outcomesMin: args.outcomesMin ?? null, outcomesMax: args.outcomesMax ?? null, createdAfter: args.createdAfter ?? null, createdBefore: args.createdBefore ?? null, sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null, limit: Number(args.limit ?? 24) }, ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetProposalsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetProposals',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['proposals', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetProposalsArgs, PagedData<GetProposalsRow>>;


export const useGetProposalsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/proposals',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetProposalsFactory },
  parseInput: (raw: unknown): useGetProposalsArgs => raw as useGetProposalsArgs,
  parseResponse: (raw: unknown): PagedData<GetProposalsRow> => raw as PagedData<GetProposalsRow>,
  specName: 'useGetProposals' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['proposals', 'entity', id] as readonly unknown[],
  viewerOverlay: {
    identityFields: ['myVote'] as const,
    walletFields: [] as const,
  },
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetProposalsRow>>;
export const useGetProposalsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 24,
  maxPageSize: 24,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetProposalsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetProposalsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetProposalsPerEntitySubjects(args: useGetProposalsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.market.*.lifecycle.v0', 'morfi.market.*.user.*.backing.v0', ...((args as Record<string, unknown>)['sortBy'] === 'createdAt' ? ['morfi.market.created.v0'] : []), ...((args as Record<string, unknown>)['status'] === 'live' ? ['morfi.market.*.lifecycle.v0'] : [])];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetProposalsRowSubjectTemplates(_args: useGetProposalsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = ['morfi.market.{pubkey}.lifecycle.v0', 'morfi.market.{pubkey}.user.*.backing.v0'];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetProposalsIsTerminalEvent(
  ev: MorfiEvent<unknown>,
  args: useGetProposalsArgs,
): boolean {
  const subject = ev.subject;
  if (typeof subject !== 'string') return false;
  if ((args as Record<string, unknown>)['excludeStatus'] === "live" && /^morfi\.market\.([^.]+)\.lifecycle\.v0$/.test(subject)) {
    try {
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
      if (["2","5"].includes(String((((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['kind']) ?? ((raw as Record<string, unknown> | undefined)?.['kind']))))) return true;
    } catch {
      return false;
    }
  }
  return false;
}

function _useGetProposalsApplyEvent(
  prev: PagedData<GetProposalsRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetProposalsArgs,
): PagedData<GetProposalsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.lifecycle.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.lifecycle\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const rowId = _m[1];
      // Lookup by id (single pass, O(N)). Skip the allocation-heavy
      // `.map(...)` path when the event's row isn't in the visible set
      // — most realtime traffic targets rows the user can't currently see.
      let _idx = -1;
      let _scanSteps = 0;
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        _scanSteps += 1;
        if ((prevRows[_i] as { pubkey?: string }).pubkey === rowId) {
          _idx = _i;
          break;
        }
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.rowScanSteps', _scanSteps);
      if (_idx === -1) {
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('applyEvent.rowScanMisses');
          _bumpPerf('applyEvent.earlyReturns');
        }
        return prev;
      }
      const row = prevRows[_idx] as GetProposalsRow;
      let updated: GetProposalsRow = row;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
        updated = (HELPERS.mapProposalLifecycleStatus as unknown as (prev: GetProposalsRow, raw: ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetProposalsRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
      // removeRow gate — splice this row out of the cache when the
      // generated terminal-event predicate matches. The patch above still
      // ran (the row's transient
      // updated identity briefly lives in `updated` before the splice
      // — useful for animation hooks that want to see the new field
      // value before the row disappears). When the gate doesn't match,
      // fall through to the normal updated-row replacement.
      {
        if (_useGetProposalsIsTerminalEvent(ev, args)) {
          const nextRows = prevRows.slice();
          nextRows.splice(_idx, 1);
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return { ...prev, data: nextRows, _normalizedPatchedEntity: updated } as PagedData<GetProposalsRow>;
        }
      }
      if (updated === row) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      // Build the new row array via splice-style clone — one
      // allocation, no per-row callbacks. Identity of every other row
      // is preserved so child memos / select observers don't churn.
      const nextRows = prevRows.slice();
      nextRows[_idx] = updated;
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}


function _useGetProposalsOnEvent(
  args: useGetProposalsArgs,
  queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown, options?: { updatedAt?: number; refreshGeneration?: number }) => void },
): (ev: MorfiEvent<unknown>) => void {
  const p0 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p1 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p2 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  return (ev: MorfiEvent<unknown>): void => {
    const subject = ev.subject;
    if (typeof subject !== 'string') return;
    // Each prepend branch decodes its own row var with ITS channel's
    // @morfi/proto schema (different literals may carry different
    // messages) — see emitDecodeRaw. No shared decode here.
      // morfi.market.${m}.user.*.backing.v0
      if (/^morfi\.market\.[^.]+\.user\.[^.]+\.backing\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBackingSchema, _d)
            : fromBinary(MarketBackingSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBackingSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p0.pending.add(_id);
        if (p0.timer === null) {
          p0.timer = setTimeout(() => {
            p0.timer = null;
            const ids = [...p0.pending];
            p0.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetProposalsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/proposals`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.marketType !== undefined) params.set('marketType', Array.isArray(args.marketType) ? args.marketType.join(',') : String(args.marketType));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.excludeStatus !== undefined) params.set('excludeStatus', Array.isArray(args.excludeStatus) ? args.excludeStatus.join(',') : String(args.excludeStatus));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.location !== undefined) params.set('location', Array.isArray(args.location) ? args.location.join(',') : String(args.location));
              if (args.search !== undefined) params.set('search', Array.isArray(args.search) ? args.search.join(',') : String(args.search));
              if (args.creator !== undefined) params.set('creator', Array.isArray(args.creator) ? args.creator.join(',') : String(args.creator));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              if (args.fundingMin !== undefined) params.set('fundingMin', Array.isArray(args.fundingMin) ? args.fundingMin.join(',') : String(args.fundingMin));
              if (args.fundingMax !== undefined) params.set('fundingMax', Array.isArray(args.fundingMax) ? args.fundingMax.join(',') : String(args.fundingMax));
              if (args.stakedMin !== undefined) params.set('stakedMin', Array.isArray(args.stakedMin) ? args.stakedMin.join(',') : String(args.stakedMin));
              if (args.stakedMax !== undefined) params.set('stakedMax', Array.isArray(args.stakedMax) ? args.stakedMax.join(',') : String(args.stakedMax));
              if (args.backersMin !== undefined) params.set('backersMin', Array.isArray(args.backersMin) ? args.backersMin.join(',') : String(args.backersMin));
              if (args.backersMax !== undefined) params.set('backersMax', Array.isArray(args.backersMax) ? args.backersMax.join(',') : String(args.backersMax));
              if (args.outcomesMin !== undefined) params.set('outcomesMin', Array.isArray(args.outcomesMin) ? args.outcomesMin.join(',') : String(args.outcomesMin));
              if (args.outcomesMax !== undefined) params.set('outcomesMax', Array.isArray(args.outcomesMax) ? args.outcomesMax.join(',') : String(args.outcomesMax));
              if (args.createdAfter !== undefined) params.set('createdAfter', Array.isArray(args.createdAfter) ? args.createdAfter.join(',') : String(args.createdAfter));
              if (args.createdBefore !== undefined) params.set('createdBefore', Array.isArray(args.createdBefore) ? args.createdBefore.join(',') : String(args.createdBefore));
              if (args.sortBy !== undefined) params.set('sortBy', Array.isArray(args.sortBy) ? args.sortBy.join(',') : String(args.sortBy));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetProposalsRow> | undefined;
              const requestedLimit = Number(args.limit ?? 24);
              const loadedCount = cached?.data.length ?? requestedLimit;
              params.set(
                'limit',
                String(Math.max(requestedLimit, Math.min(loadedCount, 24))),
              );
              const refreshGeneration = nextFeedRefreshGeneration();
              const observedAtMs = Date.now();
              const fetched: GetProposalsRow[] = [];
              let json: { data?: ReadonlyArray<GetProposalsRow> | Record<string, GetProposalsRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetProposals.refresh',
                routeTemplate: '/v1/proposals',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetProposals refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetProposalsRow> | Record<string, GetProposalsRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 24)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetProposalsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetProposalsRow>;
              }, { updatedAt: observedAtMs, refreshGeneration });
            })();
          }, 1000);
        }
      }
      // morfi.market.created.v0
      if (/^morfi\.market\.created\.v0$/.test(subject)) {
        if ((args as Record<string, unknown>)['sortBy'] !== 'createdAt') return;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketCreatedNotificationSchema, _d)
            : fromBinary(MarketCreatedNotificationSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketCreatedNotificationSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p1.pending.add(_id);
        if (p1.timer === null) {
          p1.timer = setTimeout(() => {
            p1.timer = null;
            const ids = [...p1.pending];
            p1.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetProposalsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/proposals`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.marketType !== undefined) params.set('marketType', Array.isArray(args.marketType) ? args.marketType.join(',') : String(args.marketType));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.excludeStatus !== undefined) params.set('excludeStatus', Array.isArray(args.excludeStatus) ? args.excludeStatus.join(',') : String(args.excludeStatus));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.location !== undefined) params.set('location', Array.isArray(args.location) ? args.location.join(',') : String(args.location));
              if (args.search !== undefined) params.set('search', Array.isArray(args.search) ? args.search.join(',') : String(args.search));
              if (args.creator !== undefined) params.set('creator', Array.isArray(args.creator) ? args.creator.join(',') : String(args.creator));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              if (args.fundingMin !== undefined) params.set('fundingMin', Array.isArray(args.fundingMin) ? args.fundingMin.join(',') : String(args.fundingMin));
              if (args.fundingMax !== undefined) params.set('fundingMax', Array.isArray(args.fundingMax) ? args.fundingMax.join(',') : String(args.fundingMax));
              if (args.stakedMin !== undefined) params.set('stakedMin', Array.isArray(args.stakedMin) ? args.stakedMin.join(',') : String(args.stakedMin));
              if (args.stakedMax !== undefined) params.set('stakedMax', Array.isArray(args.stakedMax) ? args.stakedMax.join(',') : String(args.stakedMax));
              if (args.backersMin !== undefined) params.set('backersMin', Array.isArray(args.backersMin) ? args.backersMin.join(',') : String(args.backersMin));
              if (args.backersMax !== undefined) params.set('backersMax', Array.isArray(args.backersMax) ? args.backersMax.join(',') : String(args.backersMax));
              if (args.outcomesMin !== undefined) params.set('outcomesMin', Array.isArray(args.outcomesMin) ? args.outcomesMin.join(',') : String(args.outcomesMin));
              if (args.outcomesMax !== undefined) params.set('outcomesMax', Array.isArray(args.outcomesMax) ? args.outcomesMax.join(',') : String(args.outcomesMax));
              if (args.createdAfter !== undefined) params.set('createdAfter', Array.isArray(args.createdAfter) ? args.createdAfter.join(',') : String(args.createdAfter));
              if (args.createdBefore !== undefined) params.set('createdBefore', Array.isArray(args.createdBefore) ? args.createdBefore.join(',') : String(args.createdBefore));
              if (args.sortBy !== undefined) params.set('sortBy', Array.isArray(args.sortBy) ? args.sortBy.join(',') : String(args.sortBy));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetProposalsRow> | undefined;
              const requestedLimit = Number(args.limit ?? 24);
              const loadedCount = cached?.data.length ?? requestedLimit;
              params.set(
                'limit',
                String(Math.max(requestedLimit, Math.min(loadedCount, 24))),
              );
              const refreshGeneration = nextFeedRefreshGeneration();
              const observedAtMs = Date.now();
              const fetched: GetProposalsRow[] = [];
              let json: { data?: ReadonlyArray<GetProposalsRow> | Record<string, GetProposalsRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetProposals.refresh',
                routeTemplate: '/v1/proposals',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetProposals refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetProposalsRow> | Record<string, GetProposalsRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 24)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetProposalsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetProposalsRow>;
              }, { updatedAt: observedAtMs, refreshGeneration });
            })();
          }, 1000);
        }
      }
      // morfi.market.${m}.lifecycle.v0
      if (/^morfi\.market\.[^.]+\.lifecycle\.v0$/.test(subject)) {
        if ((args as Record<string, unknown>)['status'] !== 'live') return;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p2.pending.add(_id);
        if (p2.timer === null) {
          p2.timer = setTimeout(() => {
            p2.timer = null;
            const ids = [...p2.pending];
            p2.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetProposalsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/proposals`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.marketType !== undefined) params.set('marketType', Array.isArray(args.marketType) ? args.marketType.join(',') : String(args.marketType));
              if (args.status !== undefined) params.set('status', Array.isArray(args.status) ? args.status.join(',') : String(args.status));
              if (args.excludeStatus !== undefined) params.set('excludeStatus', Array.isArray(args.excludeStatus) ? args.excludeStatus.join(',') : String(args.excludeStatus));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.location !== undefined) params.set('location', Array.isArray(args.location) ? args.location.join(',') : String(args.location));
              if (args.search !== undefined) params.set('search', Array.isArray(args.search) ? args.search.join(',') : String(args.search));
              if (args.creator !== undefined) params.set('creator', Array.isArray(args.creator) ? args.creator.join(',') : String(args.creator));
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              if (args.fundingMin !== undefined) params.set('fundingMin', Array.isArray(args.fundingMin) ? args.fundingMin.join(',') : String(args.fundingMin));
              if (args.fundingMax !== undefined) params.set('fundingMax', Array.isArray(args.fundingMax) ? args.fundingMax.join(',') : String(args.fundingMax));
              if (args.stakedMin !== undefined) params.set('stakedMin', Array.isArray(args.stakedMin) ? args.stakedMin.join(',') : String(args.stakedMin));
              if (args.stakedMax !== undefined) params.set('stakedMax', Array.isArray(args.stakedMax) ? args.stakedMax.join(',') : String(args.stakedMax));
              if (args.backersMin !== undefined) params.set('backersMin', Array.isArray(args.backersMin) ? args.backersMin.join(',') : String(args.backersMin));
              if (args.backersMax !== undefined) params.set('backersMax', Array.isArray(args.backersMax) ? args.backersMax.join(',') : String(args.backersMax));
              if (args.outcomesMin !== undefined) params.set('outcomesMin', Array.isArray(args.outcomesMin) ? args.outcomesMin.join(',') : String(args.outcomesMin));
              if (args.outcomesMax !== undefined) params.set('outcomesMax', Array.isArray(args.outcomesMax) ? args.outcomesMax.join(',') : String(args.outcomesMax));
              if (args.createdAfter !== undefined) params.set('createdAfter', Array.isArray(args.createdAfter) ? args.createdAfter.join(',') : String(args.createdAfter));
              if (args.createdBefore !== undefined) params.set('createdBefore', Array.isArray(args.createdBefore) ? args.createdBefore.join(',') : String(args.createdBefore));
              if (args.sortBy !== undefined) params.set('sortBy', Array.isArray(args.sortBy) ? args.sortBy.join(',') : String(args.sortBy));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetProposalsRow> | undefined;
              const requestedLimit = Number(args.limit ?? 24);
              const loadedCount = cached?.data.length ?? requestedLimit;
              params.set(
                'limit',
                String(Math.max(requestedLimit, Math.min(loadedCount, 24))),
              );
              const refreshGeneration = nextFeedRefreshGeneration();
              const observedAtMs = Date.now();
              const fetched: GetProposalsRow[] = [];
              let json: { data?: ReadonlyArray<GetProposalsRow> | Record<string, GetProposalsRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetProposals.refresh',
                routeTemplate: '/v1/proposals',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetProposals refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetProposalsRow> | Record<string, GetProposalsRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 24)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetProposalsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetProposalsRow>;
              }, { updatedAt: observedAtMs, refreshGeneration });
            })();
          }, 1000);
        }
      }
  };
}
export const useGetProposalsRealtime = {
  domain: 'proposals' as const,
  domainPins: ['morfi.market.*.lifecycle.v0'] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetProposalsArgs) => _useGetProposalsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetProposalsArgs) => _useGetProposalsRowSubjectTemplates(args),
  isTerminalEvent:
    (args: useGetProposalsArgs) =>
    (ev: MorfiEvent<unknown>): boolean =>
      _useGetProposalsIsTerminalEvent(ev, args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetProposalsArgs) =>
    (prev: PagedData<GetProposalsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetProposalsRow> | undefined =>
      _useGetProposalsApplyEvent(prev, ev, _args),
  onEvent: (args: useGetProposalsArgs, queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown, options?: { updatedAt?: number; refreshGeneration?: number }) => void }) =>
    _useGetProposalsOnEvent(args, queryClient),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetProposalsRow>, unknown>;


// ─── useGetProposal ────────────────────────────────────────────────────────────

export interface useGetProposalArgs { readonly pubkey: string; readonly viewerWallet?: string; readonly fields?: readonly ('marketType' | 'partnerCreatorId' | 'associatedCreator' | 'pubkey' | 'marketConfig' | 'proposalId' | 'title' | 'description' | 'category' | 'location' | 'status' | 'coverImageUrl' | 'coverImageNsfw' | 'numOutcomes' | 'outcomeLabels' | 'totalFunded' | 'totalStaked' | 'creationThresholdPct' | 'backerCount' | 'numBackers' | 'debateSpreadOk' | 'graduatedMarketPubkey' | 'createdAt' | 'updatedAt' | 'expiresAt' | 'upvotes' | 'downvotes' | 'netVotes' | 'myVote' | 'proposalScore' | 'viewerShareBalance' | 'outcomes' | 'proposer' | 'topBackers' | 'backers')[]; }


async function _useGetProposalQueryFn(
  args: useGetProposalArgs,
  ctx: QueryFunctionContext,
): Promise<GetProposalRow | null> {
  if (args.pubkey === undefined || args.pubkey === null || args.pubkey === '') return null;
  const url = `/v1/proposals/${args.pubkey}`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  if (args.viewerWallet !== undefined) params.set('viewerWallet', String(args.viewerWallet));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetProposal',
    routeTemplate: '/v1/proposals/{pubkey}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetProposalRow;
}


const _useGetProposalFactory = Object.assign(
  (args: useGetProposalArgs) => ({
    queryKey: ['proposals', 'entity', args.pubkey] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetProposalQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.data === null ||
      typeof query.state.data !== 'object' ||
      !('marketType' in query.state.data) ||
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetProposal',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['proposals', 'entity', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetProposalArgs, GetProposalRow>;


export const useGetProposalSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/proposals/{pubkey}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetProposalFactory },
  parseInput: (raw: unknown): useGetProposalArgs => raw as useGetProposalArgs,
  parseResponse: (raw: unknown): GetProposalRow => raw as GetProposalRow,
  specName: 'useGetProposal' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — the detail hook reads and writes the same canonical entity as
  // every normalized market list. The completeness guard above still forces
  // a detail fetch when this key currently holds only the narrower list DTO.
  canonicalEntity: true as const,
  entityQueryKey: (id: string) => ['proposals', 'entity', id] as readonly unknown[],
  viewerOverlay: {
    identityFields: ['myVote'] as const,
    walletFields: ['viewerShareBalance'] as const,
    walletArg: 'viewerWallet' as const,
  },
} as unknown as HookSpec<JsonSerialisableArgs, GetProposalRow>;

function _useGetProposalPerEntitySubjects(args: useGetProposalArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.backing.v0`, `morfi.market.${args.pubkey}.lifecycle.v0`, `morfi.market.${args.pubkey}.outcome_added.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetProposalReconcileAlwaysSubjects(args: useGetProposalArgs): readonly string[] {
  const _subs: readonly string[] = [`morfi.market.${args.pubkey}.user.*.backing.v0`];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetProposalApplyEvent(
  prev: GetProposalRow | null | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetProposalArgs,
): GetProposalRow | null | undefined {
  // A null row (e.g. a 401 → anonymous result) has nothing to patch.
  if (prev === undefined || prev === null) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetProposalRow = prev;
    // subject "morfi.market.${m}.user.*.backing.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.backing\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketBackingSchema, _d)
            : fromBinary(MarketBackingSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketBackingSchema>>;
        next = (HELPERS.applyViewerShareBalanceDelta as unknown as (prev: GetProposalRow, raw: ReturnType<typeof fromBinary<typeof MarketBackingSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetProposalRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.market.${m}.lifecycle.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.lifecycle\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketLifecycleSchema, _d)
            : fromBinary(MarketLifecycleSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
        next = (HELPERS.mapProposalLifecycleStatus as unknown as (prev: GetProposalRow, raw: ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetProposalRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // subject "morfi.market.${m}.outcome_added.v0"
  {
    const _m = subject.match(/^morfi\.market\.([^.]+)\.outcome_added\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(OutcomeAddedSchema, _d)
            : fromBinary(OutcomeAddedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof OutcomeAddedSchema>>;
        next = (HELPERS.appendOutcomeToProposal as unknown as (prev: GetProposalRow, raw: ReturnType<typeof fromBinary<typeof OutcomeAddedSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetProposalRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  return next;
}

export const useGetProposalRealtime = {
  domain: 'proposals' as const,
  domainPins: [] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetProposalArgs) => _useGetProposalPerEntitySubjects(args),
  reconcileAlwaysSubjects: (args: useGetProposalArgs) => _useGetProposalReconcileAlwaysSubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetProposalArgs) =>
    (prev: GetProposalRow | null | undefined, ev: MorfiEvent<unknown>): GetProposalRow | null | undefined =>
      _useGetProposalApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetProposalRow, unknown>;
